import { ReactNode, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ICON_LEGEND_BY_KEY } from '@/config/iconLegend';
import { cn } from '@/lib/utils';

interface IconTooltipProps {
  /** Legend key from `iconLegend.tsx`. If provided, name + description are sourced from the legend. */
  legendKey?: string;
  /** Override label (used as visible tooltip + aria-label). Falls back to the legend entry name. */
  label?: string;
  /** Override description (longer body of the tooltip). Falls back to the legend entry description. */
  description?: string;
  children: ReactNode;
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Wraps an icon/badge with a hover + tap-to-show tooltip and an accessible label.
 * Sources copy from the central icon legend so the legend page and tooltips never drift.
 */
function IconTooltip({
  legendKey,
  label,
  description,
  children,
  className,
  side = 'top',
}: IconTooltipProps) {
  const [open, setOpen] = useState(false);
  const entry = legendKey ? ICON_LEGEND_BY_KEY[legendKey] : undefined;
  const resolvedLabel = label ?? entry?.name ?? '';
  const resolvedDescription = description ?? entry?.description ?? '';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            role="img"
            aria-label={
              resolvedDescription
                ? `${resolvedLabel}: ${resolvedDescription}`
                : resolvedLabel
            }
            className={cn('inline-flex items-center justify-center', className)}
            onClick={(e) => {
              // Tap-to-show on touch devices; also serves keyboard activation
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen((prev) => !prev);
              }
            }}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent side={side} className="max-w-[240px] text-xs z-50">
            <div className="font-semibold">{resolvedLabel}</div>
            {resolvedDescription ? (
              <div className="mt-0.5 text-muted-foreground">
                {resolvedDescription}
              </div>
            ) : null}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
}

export default IconTooltip;
